import { Button, Modal, Radio, Stack, TextInput } from '@mantine/core';
import {
  IconArrowsExchange,
  IconBroadcast,
  IconMessage2,
  IconPointFilled,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { usePublishMarket } from './RfqMarket.js';
import { formatStampLocal } from './requisitions.js';
import {
  localToUtcIso,
  type PublishFormValues,
  publishFormDefaults,
  shortHash,
  timeLeft,
  validatePublishForm,
} from './rfqs.js';
import type { RfqSource } from './source.js';
import type { RfqDetail, RfqLineCell, RfqQuotation, RfqQuotationLine } from './types.js';

/**
 * Tedarikçi portalının ofis tarafı (ADR 0111 §C; plan §3/C "Ofis"): RFQ kartında portal durumu,
 * yayın penceresi (açık ya da davetli, son tarih ve saat), satır bazında karşılaştırma ("Line
 * Comparison": satır × tedarikçi, satırın en iyi fiyatı, ikame ayrı işaretli, ret ve fiyatsız
 * rozetli) ve portal teklifinin değiştirilemez sürüm geçmişi.
 */

const useK = () => {
  const { t } = useTranslation();
  return (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.${key}`, options);
};

/** Kartın başlığında portal durumu: "Supplier Portal · Open · Closes 25.10.2026 17:00". */
export function PortalStatus({ detail, now }: { detail: RfqDetail; now: Date }) {
  const k = useK();
  if (!detail.portalPublishedAt || !detail.closingAt || !detail.visibility) return null;
  const left = timeLeft(detail.closingAt, now);
  const stamp = formatStampLocal(detail.closingAt);
  return (
    <p className={classes.portalStatus} data-testid="portal-status">
      <StatusBadge tone={left ? 'info' : 'neutral'} icon={<IconBroadcast size={12} />}>
        {k('portal.label')}
      </StatusBadge>
      <span className={classes.portalFact}>
        {k(detail.visibility === 'open' ? 'portal.open' : 'portal.invited')}
      </span>
      <span className={classes.portalFact}>
        {left ? k('portal.closes', { date: stamp }) : k('portal.closed', { date: stamp })}
      </span>
      {left ? (
        <span className={classes.portalLeft} data-soon={left.days < 1 || undefined}>
          {k('portal.left', left)}
        </span>
      ) : null}
    </p>
  );
}

/** Yayın penceresi: kimler teklif verir, son tarih (yerel gün ve saat → UTC). */
export function PublishDialog({
  detail,
  source,
  now,
  onClose,
  onPublished,
}: {
  detail: RfqDetail;
  source: RfqSource;
  now: Date;
  onClose: () => void;
  onPublished: (next: RfqDetail) => void;
}) {
  const { t } = useTranslation();
  const k = useK();
  const [values, setValues] = useState<PublishFormValues>(() => publishFormDefaults(now));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const names = detail.suppliers.map((s) => s.name).join(', ') || '—';
  // Açık pazar: teslim bölgesi ve bölge süzgeçli davet (ADR 0113)
  const market = usePublishMarket(detail, source);
  const fieldError = (field: string) =>
    errors[field] ? k(`portal.fieldErrors.${errors[field]}`) : undefined;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('portal.publishTitle')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          const found = validatePublishForm(values, new Date());
          setErrors(found);
          const marketErrors = market.validate(values.visibility);
          if (Object.keys(found).length + Object.keys(marketErrors).length > 0) return;
          if (!source.publish) return;
          setBusy(true);
          setError(null);
          try {
            onPublished(
              await source.publish(detail.id, {
                rowVersion: detail.rowVersion,
                visibility: values.visibility,
                closingAt: localToUtcIso(values.date, values.time) ?? '',
                ...market.payload(values.visibility),
              }),
            );
          } catch (failure) {
            setError(errorCode(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Stack gap="md">
          <p className={classes.muted}>{k('portal.publishBody')}</p>
          <Radio.Group
            label={k('portal.visibility')}
            value={values.visibility}
            onChange={(value) =>
              setValues((v) => ({ ...v, visibility: value as PublishFormValues['visibility'] }))
            }
          >
            <Stack gap="xs" mt={6}>
              <Radio
                value="invited"
                label={k('portal.invitedOption')}
                description={k('portal.invitedHint', { names })}
              />
              <Radio
                value="open"
                label={k('portal.openOption')}
                description={k('portal.openHint')}
              />
            </Stack>
          </Radio.Group>
          {market.render(values.visibility)}
          <fieldset className={classes.deadline}>
            <legend>{k('portal.deadline')}</legend>
            <TextInput
              type="date"
              label={k('portal.date')}
              value={values.date}
              onChange={(event) => {
                const date = event.currentTarget.value;
                setValues((v) => ({ ...v, date }));
                // Düzeltilen alanın eski hatası kalmasın (son tarih tek kural: ikisini de temizle)
                setErrors({});
              }}
              error={fieldError('date')}
              required
            />
            <TextInput
              type="time"
              label={k('portal.time')}
              value={values.time}
              onChange={(event) => {
                const time = event.currentTarget.value;
                setValues((v) => ({ ...v, time }));
                // Düzeltilen alanın eski hatası kalmasın (son tarih tek kural: ikisini de temizle)
                setErrors({});
              }}
              error={fieldError('time')}
              required
            />
            <p className={classes.deadlineHint}>{k('portal.timeHint', { zone })}</p>
          </fieldset>
          {error ? (
            <p className={classes.error} role="alert">
              {t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
            </p>
          ) : null}
        </Stack>
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy} leftSection={<IconBroadcast size={16} />}>
            {k('portal.confirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Teklifin kaynağı ve portal sürümü: "Portal · Rev 2" ya da "Office". */
export function SourceBadge({ quotation }: { quotation: RfqQuotation }) {
  const k = useK();
  if (quotation.source === 'portal') {
    return (
      <StatusBadge tone="info" icon={<IconBroadcast size={11} />}>
        {quotation.revisionNo
          ? `${k('source.portal')} · ${k('revision', { no: quotation.revisionNo })}`
          : k('source.portal')}
      </StatusBadge>
    );
  }
  return <StatusBadge tone="neutral">{k('source.office')}</StatusBadge>;
}

/** Portal teklifinin değiştirilemez sürümleri, yeniden eskiye (ADR 0111 §C). */
export function RevisionHistory({
  quotation,
  money,
}: {
  quotation: RfqQuotation;
  money: (value: number | null) => string;
}) {
  const k = useK();
  const revisions = quotation.revisions ?? [];
  if (quotation.source !== 'portal' || revisions.length === 0) return null;
  const label = k('revisions.title', { supplier: quotation.supplierName });
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>
        <caption className={classes.revisionCaption}>{label}</caption>
        <thead>
          <tr>
            <th scope="col" className={classes.key}>
              {k('revisions.no')}
            </th>
            <th scope="col">{k('revisions.submitted')}</th>
            <th scope="col" data-numeric>
              {k('revisions.total')}
            </th>
            <th scope="col">{k('revisions.submittedBy')}</th>
            <th scope="col">{k('revisions.hash')}</th>
          </tr>
        </thead>
        <tbody>
          {revisions.map((revision, index) => (
            <tr key={revision.revisionNo}>
              <td className={classes.key}>
                {k('revision', { no: revision.revisionNo })}
                {index === 0 ? (
                  <span className={classes.flags}>
                    <StatusBadge tone="success">{k('revisions.current')}</StatusBadge>
                  </span>
                ) : null}
              </td>
              <td>{formatStampLocal(revision.submittedAt)}</td>
              <td data-numeric>{money(revision.landedTotal)}</td>
              {/* Gönderen portal hesabı; onay PIN'i kaldırıldı (sahip kararı 2026-10-23) */}
              <td className={classes.submittedBy}>{revision.submittedBy ?? '—'}</td>
              <td>
                <code className={classes.hash} title={revision.contentHash}>
                  {shortHash(revision.contentHash)}
                </code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Hücrenin içeriği: fiyat ve toplam, ya da yanıt rozeti; ikame ayrıntısı; not. */
function CellBody({
  cell,
  line,
  money,
  quantity,
}: {
  cell: RfqLineCell;
  line: RfqQuotationLine | undefined;
  money: (value: number | null) => string;
  quantity: (value: number) => string;
}) {
  const k = useK();
  const note = line?.note ? (
    <span className={classes.cellNote}>
      <IconMessage2 size={12} aria-hidden />
      <span className={classes.visuallyHidden}>{k('lines.noteLabel')}: </span>
      {line.note}
    </span>
  ) : null;
  let body: ReactNode;
  switch (cell.response) {
    case null:
      body = <span className={classes.muted}>{k('lines.noAnswer')}</span>;
      break;
    case 'priced':
      body = (
        <>
          <span className={classes.cellPrice}>{money(cell.unitPrice)}</span>
          <span className={classes.cellTotal}>= {money(cell.total)}</span>
          {cell.best ? (
            <StatusBadge tone="success" icon={<IconPointFilled size={10} />}>
              {k('lines.best')}
            </StatusBadge>
          ) : null}
        </>
      );
      break;
    case 'no_price':
      body = <StatusBadge tone="neutral">{k('lines.noPrice')}</StatusBadge>;
      break;
    case 'declined':
      body = <StatusBadge tone="danger">{k('lines.declined')}</StatusBadge>;
      break;
    case 'substitute': {
      const sub = line?.substitute;
      body = (
        <>
          <StatusBadge tone="info" icon={<IconArrowsExchange size={11} />}>
            {k('lines.substitute')}
          </StatusBadge>
          {sub ? (
            <span className={classes.cellSub}>
              <span className={classes.cellSubName}>{sub.name}</span>
              {sub.specification ? (
                <span className={classes.muted}>{sub.specification}</span>
              ) : null}
              <span>
                {sub.quantity === null ? '—' : quantity(sub.quantity)} {sub.unit} ×{' '}
                {money(sub.unitPrice)} = {money(sub.total)}
              </span>
            </span>
          ) : null}
        </>
      );
      break;
    }
  }
  return (
    <span className={classes.cell}>
      {body}
      {!cell.eligible && cell.reason ? (
        <span className={classes.muted}>
          {k(cell.reason === 'QUOTATION_CURRENCY' ? 'lines.otherCurrency' : 'lines.notApproved')}
        </span>
      ) : null}
      {note}
    </span>
  );
}

/**
 * Satır bazında karşılaştırma (sahip, 2026-10-23): satırlar RFQ kalemleri, sütunlar teklifler.
 * Satırın en iyi fiyatı yeşil zeminli ve "Best" rozetli; ikame "Substitute" rozetiyle ayrı, en iyi
 * sayılmaz; ret ve fiyatsız rozetli. Altta satır başına en iyilerin toplamı. Dar ekranda tablo
 * kendi kutusunda yatay kayar, kalem sütunu sabit (Responsive kurallar).
 */
export function LineComparison({
  detail,
  money,
  quantity,
}: {
  detail: RfqDetail;
  money: (value: number | null) => string;
  quantity: (value: number) => string;
}) {
  const k = useK();
  const comparison = detail.lineComparison;
  if (!comparison || detail.quotations.length === 0) {
    return <EmptyState title={k('lines.empty')} />;
  }
  const byLine = new Map(comparison.lines.map((row) => [row.rfqLineId, row]));
  const quotations = detail.quotations;
  return (
    <>
      <p className={classes.note}>{k('lines.note', { currency: detail.currency })}</p>
      <section
        className={`${table.tableWrap} ${classes.boxed}`}
        aria-label={k('lines.title')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${classes.table} ${classes.lineMatrix}`}>
          <thead>
            <tr>
              <th scope="col" className={classes.key}>
                {k('columns.material')}
              </th>
              {quotations.map((q) => (
                <th key={q.id} scope="col">
                  <span className={classes.matrixHead}>
                    <span className={classes.matrixSupplier}>{q.supplierName}</span>
                    <SourceBadge quotation={q} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.lines.map((line) => {
              const row = byLine.get(line.id);
              return (
                <tr key={line.id}>
                  <th scope="row" className={classes.key}>
                    <span className={classes.matrixItem}>
                      {line.lineNo}. {line.name}
                    </span>
                    <span className={classes.muted}>
                      {quantity(line.quantity)} {line.unit}
                    </span>
                  </th>
                  {quotations.map((q) => {
                    const cell = row?.cells.find((c) => c.quotationId === q.id);
                    return (
                      <td
                        key={q.id}
                        data-best={cell?.best || undefined}
                        data-substitute={cell?.substitute || undefined}
                        data-response={cell?.response ?? 'none'}
                      >
                        {cell ? (
                          <CellBody
                            cell={cell}
                            line={q.lines.find((l) => l.rfqLineId === line.id)}
                            money={money}
                            quantity={quantity}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr data-strong>
              <th scope="row" className={classes.key}>
                {k('lines.bestTotal')}
              </th>
              {quotations.map((q) => (
                <td key={q.id}>
                  {k('lines.bestCount', { count: comparison.bestCount[q.id] ?? 0 })}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </section>
      <dl className={classes.totals}>
        <div>
          <dt>{k('lines.covered')}</dt>
          <dd>{`${comparison.coveredLines} / ${detail.lines.length}`}</dd>
        </div>
        <div data-strong>
          <dt>{`${k('lines.bestTotal')} (${detail.currency})`}</dt>
          <dd>{money(comparison.bestTotal)}</dd>
        </div>
      </dl>
    </>
  );
}
